import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BrainCircuit, Loader2, Plus, Trash2 } from 'lucide-react';
import { trainingApi } from '../services/api';
import TrainingTabs from '../components/training/TrainingTabs';

/**
 * Plan a campaign (EP-26 FE-17 ST-6): choose the stream, name it, and bundle units into
 * batches -- released atoms for an atom stream (with their evidence and gold readiness),
 * compound families with a chosen mode for a compound stream. The campaign is made from
 * the programme's process template by a background job; the Programme map then shows it.
 */
type D = Record<string, any>;

export default function TrainingCampaignNewPage() {
  const { programId = '' } = useParams<{ programId: string }>();
  const navigate = useNavigate();
  const [programme, setProgramme] = useState<D | null>(null);
  const [atoms, setAtoms] = useState<D[]>([]);
  const [name, setName] = useState('');
  const [stream, setStream] = useState('');
  const [batches, setBatches] = useState<string[][]>([[]]);
  const [current, setCurrent] = useState(0);
  const [layer, setLayer] = useState('');
  const [query, setQuery] = useState('');
  const [onlyReady, setOnlyReady] = useState(false);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    trainingApi.programme(programId).then((p: D) => {
      setProgramme(p);
      const ss = p.profile?.streams || [];
      if (ss.length) setStream(ss[0].key);
    }).catch((e) => setStatus(String(e?.message || e)));
    trainingApi.dashboard(programId, 'atoms').then((d: D) => setAtoms(d.atoms || [])).catch(() => {});
  }, [programId]);

  const streams: D[] = programme?.profile?.streams || [];
  const s = streams.find((x) => x.key === stream);
  const compound = s?.unit === 'compound_family';
  const families = Object.keys(s?.familyModes || {});
  const taken = useMemo(() => new Set(batches.flat()), [batches]);
  const layers = useMemo(() => Array.from(new Set(atoms.map((a) => a.layer).filter(Boolean))).sort(), [atoms]);
  const ready = (a: D) => !a.below_floor && (a.gold || 0) > 0;
  const shown = atoms.filter((a) => (!layer || a.layer === layer) && (!onlyReady || ready(a))
    && (!query || `${a.ref} ${a.family || ''}`.toLowerCase().includes(query.toLowerCase())));

  const add = (refs: string[]) => setBatches((b) => b.map((x, i) => (i === current ? Array.from(new Set([...x, ...refs.filter((r) => !taken.has(r))])) : x)));
  const remove = (bi: number, ref: string) => setBatches((b) => b.map((x, i) => (i === bi ? x.filter((r) => r !== ref) : x)));

  const create = async () => {
    setBusy(true); setStatus('');
    try {
      const out = await trainingApi.createCampaign(programId, { name: name.trim(), stream, batches: batches.filter((b) => b.length), background: true });
      setStatus(`Creating ${out.items} work items…`);
      for (let i = 0; i < 300; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const j = await trainingApi.job(programId, out.jobId);
        if (j.state === 'done') { navigate(`/training/${encodeURIComponent(programId)}`); return; }
        if (j.state === 'failed') { setStatus(`Not created: ${j.error}. Creating it again finishes what was made.`); setBusy(false); return; }
      }
      setStatus('Still creating; it will appear on the Programme map when done.');
    } catch (e: any) {
      setStatus(e?.message || 'Not created.');
    }
    setBusy(false);
  };

  const filled = batches.filter((b) => b.length);
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2"><BrainCircuit className="w-5 h-5" /> Plan a campaign</h1>
      <TrainingTabs programId={programId} />
      <p className="text-sm text-gray-600 dark:text-gray-400">
        A campaign takes bundles of units (batches) through the process. Each batch goes through the data steps; round k trains on
        batches 1..k and is evaluated on all of them. Pick the stream, then fill the batches.
      </p>
      <div className="grid gap-3 md:grid-cols-3">
        <label className="text-xs text-gray-600 dark:text-gray-400">Name
          <input className="mt-0.5 block w-full px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Atom stream MVP-2" /></label>
        {streams.length > 0 && (
          <label className="text-xs text-gray-600 dark:text-gray-400">Stream
            <select className="mt-0.5 block w-full px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600" value={stream}
              onChange={(e) => { setStream(e.target.value); setBatches([[]]); setCurrent(0); }}>
              {streams.map((x) => <option key={x.key} value={x.key}>{x.label || x.key}</option>)}
            </select></label>)}
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr,22rem]">
        <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          {compound ? (<>
            <h3 className="text-sm font-semibold mb-2">Compound families (with a mode chosen in the profile)</h3>
            {!families.length && <p className="text-xs text-gray-500">No family has a mode yet: propose one on the Profile tab (streams.familyModes).</p>}
            {families.map((f) => (
              <div key={f} className="flex items-center gap-2 text-sm py-0.5">
                <code>{f}</code><span className="text-xs text-gray-500">{s?.familyModes?.[f]}</span>
                <button type="button" disabled={taken.has(f)} onClick={() => add([f])} className="ml-auto text-xs text-accent disabled:text-gray-400">add to batch {current + 1}</button>
              </div>))}
          </>) : (<>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h3 className="text-sm font-semibold">Released atoms</h3>
              <select value={layer} onChange={(e) => setLayer(e.target.value)} className="px-2 py-0.5 text-xs border rounded dark:bg-gray-700 dark:border-gray-600">
                <option value="">All layers</option>{layers.map((l) => <option key={l}>{l}</option>)}</select>
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="search ref or family" className="px-2 py-0.5 text-xs border rounded dark:bg-gray-700 dark:border-gray-600" />
              <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={onlyReady} onChange={(e) => setOnlyReady(e.target.checked)} /> ready only</label>
              <button type="button" onClick={() => add(shown.map((a) => a.ref))} className="ml-auto text-xs text-accent">add all {shown.filter((a) => !taken.has(a.ref)).length} shown to batch {current + 1}</button>
            </div>
            <div className="max-h-[28rem] overflow-y-auto">
              <table className="w-full text-xs">
                <thead><tr className="text-left text-gray-500"><th>Atom</th><th>Layer</th><th>Family</th><th className="text-right" title="qualified learners (evidence at the floor counts these)">Qualified</th><th className="text-right">Gold</th><th>Ready</th><th /></tr></thead>
                <tbody>
                  {shown.map((a) => (
                    <tr key={a.ref} className="border-t border-gray-100 dark:border-gray-700">
                      <td className="font-mono py-0.5">{a.ref}</td><td>{a.layer}</td><td>{a.family}</td>
                      <td className="text-right">{a.qualified}</td><td className="text-right">{a.gold}</td>
                      <td>{ready(a) ? <span className="text-emerald-600">evidence + gold</span> : a.below_floor ? <span className="text-amber-700">below floor</span> : <span className="text-amber-700">no gold</span>}</td>
                      <td>{taken.has(a.ref) ? <span className="text-gray-400">in a batch</span>
                        : <button type="button" onClick={() => add([a.ref])} className="text-accent">add</button>}</td>
                    </tr>))}
                </tbody>
              </table>
            </div>
            <p className="mt-1 text-[11px] text-gray-500">"evidence + gold": the atom already has evidence at the floor and gold, so its batch needs no new generation or grading.</p>
          </>)}
        </section>

        <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
          <h3 className="text-sm font-semibold">Batches</h3>
          {batches.map((b, i) => (
            <div key={i} className={`rounded border p-2 ${i === current ? 'border-accent' : 'border-gray-200 dark:border-gray-700'}`} onClick={() => setCurrent(i)}>
              <div className="flex items-center gap-2 text-xs">
                <strong>Batch {i + 1}</strong><span className="text-gray-500">{b.length} unit(s){i === current ? ' · adding here' : ''}</span>
                {batches.length > 1 && <button type="button" className="ml-auto" title="Remove batch" onClick={(e) => { e.stopPropagation(); setBatches((x) => x.filter((_, j) => j !== i)); setCurrent(0); }}><Trash2 className="w-3.5 h-3.5 text-red-600" /></button>}
              </div>
              <div className="mt-1 flex flex-wrap gap-1">
                {b.map((r) => <button key={r} type="button" onClick={(e) => { e.stopPropagation(); remove(i, r); }} title="remove"
                  className="text-[10px] font-mono px-1 rounded bg-gray-100 dark:bg-gray-700 hover:line-through">{r}</button>)}
              </div>
            </div>))}
          <button type="button" onClick={() => { setBatches((b) => [...b, []]); setCurrent(batches.length); }} className="text-xs flex items-center gap-1 text-accent"><Plus className="w-3.5 h-3.5" /> Add a batch</button>
          <div className="text-xs text-gray-600 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-2">
            Makes {filled.length} batch(es) and {filled.length} round(s) from {programme?.profile?.processTemplate?.key || 'the process template'};
            round k trains on batches 1..k.
          </div>
          <button type="button" disabled={busy || !name.trim() || !filled.length} onClick={create}
            className="w-full px-3 py-1.5 text-sm rounded bg-accent text-white disabled:opacity-50 flex items-center justify-center gap-1">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Create the campaign</button>
          {status && <p className="text-xs text-gray-700 dark:text-gray-300">{status}</p>}
        </section>
      </div>
    </div>
  );
}
